import { assert, unreachable } from '@chainflip/utils/assertion';
import {
  assetConstants,
  chainflipAssets,
  chainflipAssetToPriceAssetMap,
  isLegacyChainflipAsset,
  type ChainflipAsset,
} from '@chainflip/utils/chainflip';
import { MAX_TICK, MIN_TICK, tickToRate } from '@chainflip/utils/tickMath';
import BigNumber from 'bignumber.js';
import { z } from 'zod';
import {
  type StrategyInfoFragment,
  type StrategyType,
} from '@/shared/graphql/generated/reporting/graphql';
import TokenAmount from '@/shared/utils/TokenAmount';
import { POSITIVE_DECIMALS_REGEX, SIGNED_INTEGERS_REGEX } from './regex';

export const MAX_BPS = 50; // 0.5%
export const MIN_BPS = -MAX_BPS; // -0.5%

const MIN_PRICE = 0.99;
const MAX_PRICE = 1.01;

export type StrategyTemplateKey = `${ChainflipAsset},Usdc,${StrategyType},${string}`;

export type StrategyTemplate = {
  templateKey: StrategyTemplateKey;
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  type: StrategyType;
  instanceCount: number;
  totalBaseLiquidity: TokenAmount;
  totalQuoteLiquidity: TokenAmount;
  totalBaseLiquidityUsd: number;
  totalQuoteLiquidityUsd: number;
  ownInstance: TradingStrategy | undefined;
  reportingData:
    | {
        totalFilledVolumeUsd: number;
        apyPercent: number;
        pnl24HourUsd: number;
      }
    | undefined;
};

export type TradingStrategy = {
  templateKey: StrategyTemplateKey;
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  type: StrategyType;
  baseLiquidity: TokenAmount;
  quoteLiquidity: TokenAmount;
  baseLiquidityUsd: number;
  quoteLiquidityUsd: number;
  totalLiquidityUsd: number;
  strategyAccountSs58Id: string;
  ownerAccountSs58Id: string;
  status: 'ACTIVE' | 'CLOSED';
  reportingData:
    | {
        filledVolumeUsd: number;
        pnlUsd: number;
        pnl24HourUsd: number;
        apyPercent: number;
        projectedReturnUsd: number;
        createdAtBlockTimestamp?: string;
        closedAtBlockTimestamp?: string;
        closedAtBlockId?: number;
        closedAtEventIndex?: number;
      }
    | undefined;
};

export type AllowedStrategyType = Extract<StrategyType, 'INVENTORY_BASED' | 'ORACLE_TRACKING'>;

export const getStrategyTickKey = ({
  minBuyTick,
  maxBuyTick,
  minSellTick,
  maxSellTick,
}: {
  minBuyTick: number;
  maxBuyTick: number;
  minSellTick: number;
  maxSellTick: number;
}) => `${minBuyTick}_${maxBuyTick}_${minSellTick}_${maxSellTick}`;

export const getStrategyTickRange = (type: StrategyType, key: string) => {
  switch (type) {
    case 'TICK_ZERO_CENTERED':
    case 'SIMPLE_BUY_SELL': {
      const [buyTick, sellTick] = key.split('_');
      return {
        minBuyTick: Number(buyTick),
        maxBuyTick: Number(buyTick),
        minSellTick: Number(sellTick),
        maxSellTick: Number(sellTick),
      };
    }
    case 'INVENTORY_BASED':
    case 'ORACLE_TRACKING': {
      const [minBuyTick, maxBuyTick, minSellTick, maxSellTick] = key.split('_');
      return {
        minBuyTick: Number(minBuyTick),
        maxBuyTick: Number(maxBuyTick),
        minSellTick: Number(minSellTick),
        maxSellTick: Number(maxSellTick),
      };
    }
    default:
      unreachable(type, `Unsupported strategy type: ${type}`);
  }
};

const findMatchingValue = <T extends string>(
  possibilities: readonly T[],
  value: string,
): T | undefined => possibilities.find((item) => item.toLowerCase() === value.toLowerCase());

const strategyTypes = [
  'ORACLE_TRACKING',
  'INVENTORY_BASED',
  'SIMPLE_BUY_SELL',
  'TICK_ZERO_CENTERED',
] as const;

export const templateKeySchema = z
  .string()
  .regex(/^[a-zA-Z0-9]+,[a-zA-Z0-9]+,[a-zA-Z_]+,[0-9-_]+$/, 'Invalid strategy key format')
  .transform((item) => {
    const parts = item.split(',');
    if (parts.length !== 4) {
      throw new Error('Invalid strategy template key format');
    }
    const baseAsset = findMatchingValue(chainflipAssets, parts[0]);
    assert(baseAsset && baseAsset !== 'Usdc', 'Invalid base asset');
    const quoteAsset = findMatchingValue(chainflipAssets, parts[1]);
    assert(quoteAsset === 'Usdc', 'Invalid quote asset');
    const type = findMatchingValue(strategyTypes, parts[2]);
    assert(type, 'Invalid strategy type');
    const key = parts[3];
    const { minBuyTick, minSellTick, maxBuyTick, maxSellTick } = getStrategyTickRange(type, key);
    return { baseAsset, quoteAsset, minBuyTick, minSellTick, maxBuyTick, maxSellTick, type, key };
  });

export type ParsedTemplateKey = z.output<typeof templateKeySchema>;

export const getStrategyTemplateUrl = (template: StrategyTemplate | TradingStrategy) =>
  `/strategies/templates/${template.templateKey}`;

export const getStrategyTemplateKey = (
  strategy: Pick<StrategyInfoFragment, 'baseAsset' | 'key' | 'type'>,
) =>
  `${strategy.baseAsset as ChainflipAsset},Usdc,${strategy.type},${strategy.key}` as StrategyTemplateKey;

export const calculateAnnualReturn = (
  totalLiquidityUsd: number | string,
  apyPercent: number | string,
) => Number(totalLiquidityUsd) * (Number(apyPercent) / 100);

export const maxWholeUnits = (a: TokenAmount, b: TokenAmount) =>
  a.toBigNumber().gte(b.toBigNumber()) ? a : b;

export function getStrategyFromReportingFragment(
  fragment: StrategyInfoFragment | undefined,
): TradingStrategy | undefined;
export function getStrategyFromReportingFragment(fragment: StrategyInfoFragment): TradingStrategy;
export function getStrategyFromReportingFragment(
  fragment: StrategyInfoFragment | undefined,
): TradingStrategy | undefined {
  if (!fragment) return undefined;

  if (isLegacyChainflipAsset(fragment.baseAsset) || isLegacyChainflipAsset(fragment.quoteAsset)) {
    return undefined;
  }

  return {
    ...fragment,
    baseAsset: fragment.baseAsset,
    quoteAsset: fragment.quoteAsset,
    templateKey: getStrategyTemplateKey(fragment),
    baseLiquidity: TokenAmount.fromAsset(fragment.baseLiquidity, fragment.baseAsset),
    quoteLiquidity: TokenAmount.fromAsset(fragment.quoteLiquidity, fragment.quoteAsset),
    baseLiquidityUsd: Number(fragment.baseLiquidityValueUsd),
    quoteLiquidityUsd: Number(fragment.quoteLiquidityValueUsd),
    totalLiquidityUsd: Number(fragment.totalLiquidityValueUsd),
    reportingData: {
      filledVolumeUsd: Number(fragment.filledVolumeValueUsd),
      pnlUsd: Number(fragment.pnlValueUsd),
      pnl24HourUsd: Number(fragment.pnl24HourValueUsd),
      apyPercent: Number(fragment.apy7Days),
      projectedReturnUsd: calculateAnnualReturn(fragment.totalLiquidityValueUsd, fragment.apy7Days),
      createdAtBlockTimestamp: fragment.createdAtBlockTimestamp ?? undefined,
      closedAtBlockTimestamp: fragment.closedAtBlockTimestamp ?? undefined,
      closedAtBlockId: fragment.closedAtBlockId ?? undefined,
      closedAtEventIndex: fragment.closedAtEventIndex ?? undefined,
    },
  };
}

export const isAllowedStrategyType = (type: StrategyType): type is AllowedStrategyType =>
  type === 'INVENTORY_BASED' || type === 'ORACLE_TRACKING';

// oracle-tracking strategies follow the oracle price of the base asset, so assets without a
// tracked price feed (e.g. BscUsdt) cannot be used for them
const assetSupportsOracleTracking = (asset: ChainflipAsset) =>
  chainflipAssetToPriceAssetMap[asset] !== null;

export const assetSupportsStrategyType = (
  type: AllowedStrategyType | undefined,
  asset: ChainflipAsset,
) => (type === 'ORACLE_TRACKING' ? assetSupportsOracleTracking(asset) : true);

const pegTick = (baseAsset: ChainflipAsset) => {
  const rateDecimals = assetConstants.Usdc.decimals - assetConstants[baseAsset].decimals;
  return Math.round((rateDecimals * Math.LN10) / Math.log(1.0001));
};

// Linear approximation: prices clamped to 0.99–1.01 keep the error <1 tick.
// Kept for compatibility with existing on-chain templates
export const customStrategyPriceToTick = (price: number | string, baseAsset: ChainflipAsset) =>
  new BigNumber(price).shiftedBy(4).minus(10e3).plus(pegTick(baseAsset)).toNumber();

// TODO: move to the toolkit
export const bpsToTick = (bps: number | string) => {
  const rawRate = 1 + Number(bps) / 10_000;
  let tick = Math.log(rawRate) / Math.log(1.0001);
  tick = Math.round(tick * 1e6) / 1e6;
  tick = Math.round(tick);
  return Math.max(MIN_TICK, Math.min(tick, MAX_TICK));
};
// TODO: move to the toolkit
export const tickToBps = (tick: number) => (1.0001 ** tick - 1) * 10_000;

export const parseBpsInput = (value?: string) => {
  if (!value) return undefined;
  if (!SIGNED_INTEGERS_REGEX.test(value)) return undefined;
  const valueToNumber = Number(value);
  return Number.isFinite(valueToNumber) ? valueToNumber : undefined;
};

export const parsePriceInput = (value?: string) => {
  if (!value) return undefined;
  if (!POSITIVE_DECIMALS_REGEX.test(value)) return undefined;
  const valueToNumber = Number(value);
  return Number.isFinite(valueToNumber) ? valueToNumber : undefined;
};

export const formatTick = (tick: number, type: StrategyType, baseAsset?: ChainflipAsset) =>
  type === 'ORACLE_TRACKING'
    ? tickToBps(tick).toFixed(0)
    : tickToRate(tick, baseAsset ?? 'Usdt').toFixed(4);

export const formatStablecoinStrategyRange = (min: string, max: string, type: StrategyType) =>
  type === 'ORACLE_TRACKING' ? `${min} / ${max} bps` : `${min} - ${max}`;

export const convertedTick = (
  minTick: number,
  maxTick: number,
  asset: ChainflipAsset,
  type: StrategyType,
) => {
  const min = formatTick(minTick, type, asset);
  const max = formatTick(maxTick, type, asset);
  return formatStablecoinStrategyRange(min, max, type);
};

const validationConfig = {
  INVENTORY_BASED: { MIN: MIN_PRICE, MAX: MAX_PRICE, label: 'Price', precision: 4 },
  ORACLE_TRACKING: { MIN: MIN_BPS, MAX: MAX_BPS, label: 'Offset', precision: 0 },
} as const;

type SpreadValues = {
  minBuy?: string;
  maxBuy?: string;
  minSell?: string;
  maxSell?: string;
};

export type SpreadValidation = {
  fieldErrors: Partial<Record<keyof SpreadValues, boolean>>;
  message?: string;
  warning?: string;
};

export const validateStrategySpread = (strategyType: AllowedStrategyType, values: SpreadValues) => {
  const { MIN, MAX, label, precision } = validationConfig[strategyType];
  const parser = strategyType === 'INVENTORY_BASED' ? parsePriceInput : parseBpsInput;

  const parsed = {
    minBuy: parser(values.minBuy),
    maxBuy: parser(values.maxBuy),
    minSell: parser(values.minSell),
    maxSell: parser(values.maxSell),
  };

  // check if all spreads are defined
  const missingFieldErrors: SpreadValidation['fieldErrors'] = {};
  if (parsed.minBuy === undefined) missingFieldErrors.minBuy = true;
  if (parsed.maxBuy === undefined) missingFieldErrors.maxBuy = true;
  if (parsed.minSell === undefined) missingFieldErrors.minSell = true;
  if (parsed.maxSell === undefined) missingFieldErrors.maxSell = true;

  if (Object.keys(missingFieldErrors).length)
    return { fieldErrors: missingFieldErrors, message: 'Spread values are required' };

  // check if all spreads are within allowed min/max range
  const { minBuy, maxBuy, minSell, maxSell } = parsed as Record<keyof typeof parsed, number>;
  const inputs = [
    { key: 'minBuy', value: minBuy, name: `Min. Buy ${label}` },
    { key: 'maxBuy', value: maxBuy, name: `Max. Buy ${label}` },
    { key: 'minSell', value: minSell, name: `Min. Sell ${label}` },
    { key: 'maxSell', value: maxSell, name: `Max. Sell ${label}` },
  ] as const;
  const errors: SpreadValidation['fieldErrors'] = {};

  for (const input of inputs) {
    if (input.value < MIN) {
      errors[input.key] = true;
      return { fieldErrors: errors, message: `${input.name} must be greater than ${MIN}` };
    }
    if (input.value > MAX) {
      errors[input.key] = true;
      return { fieldErrors: errors, message: `${input.name} must be less than ${MAX}` };
    }
  }

  // check if all spreads respect min < max
  if (minBuy > maxBuy) {
    return {
      fieldErrors: { minBuy: true, maxBuy: true },
      message: `Min. Buy ${label} cannot be greater than Max. Buy ${label}`,
    };
  }
  if (minSell > maxSell) {
    return {
      fieldErrors: { minSell: true, maxSell: true },
      message: `Min. Sell ${label} cannot be greater than Max. Sell ${label}`,
    };
  }

  // check if all spreads respect mid-point
  const avgBuy = (minBuy + maxBuy) / 2;
  const avgSell = (minSell + maxSell) / 2;
  if (minSell < avgBuy) {
    return {
      fieldErrors: { minSell: true },
      message: `Min Sell ${label} must be higher than buy mid-point (${avgBuy.toFixed(precision)})`,
    };
  }
  if (maxBuy > avgSell) {
    return {
      fieldErrors: { maxBuy: true },
      message: `Max Buy ${label} must be lower than sell mid-point (${avgSell.toFixed(precision)})`,
    };
  }

  // check if min sell > max buy
  if (minSell < maxBuy) {
    return {
      fieldErrors: {},
      warning:
        'Your buy and sell spreads overlap. This may lead to fills at a loss. Please proceed with caution.',
    };
  }

  return { fieldErrors: {} };
};

export const TOOLTIP_COPY = {
  INVENTORY_BASED: {
    general: 'Your orders execute at fixed price points you define, relative to the $1 peg.',
    buy: 'The range of prices below the mid-market rate at which the strategy buys the base asset.',
    sell: 'The range of prices above the mid-market rate at which the strategy sells the base asset.',
  },
  ORACLE_TRACKING: {
    general: 'Your orders execute within these ranges, relative to the oracle price.',
    buy: 'The range of basis-point offsets below the oracle price at which the strategy buys the base asset.',
    sell: 'The range of basis-point offsets above the oracle price at which the strategy sells the base asset.',
  },
} as const;
